import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { TranslateService } from '@ngx-translate/core';
import { IEnvironment } from '../../models/environment.model';
import { updateUrlWithApiGatewayBaseUrl } from '../../../utils/utils/data-fetch-widget-utils';
import {
  IPreFetchCheckConfig,
  IPreFetchCheckResult,
  IPreFetchPayloadKeyMapping,
  IIntegrationFetchSyncRequest,
  IIntegrationFetchSyncResponse,
  IIntegrationErrorBody,
} from '../../models/generic-data-fetch-pre-pipeline.model';

/**
 * Callback type for resolving form control values.
 * Allows the service to read form field values without depending on formly.
 */
export type ControlValueResolver = (
  sourceField: string,
  searchInRootForm?: boolean
) => unknown;

/**
 * Service responsible for executing pre-fetch checks in the guarded data fetch pipeline.
 *
 * Handles:
 * - Sequential execution of pre-fetch API calls
 * - Parsing the double-wrapped JSON response from the integration API
 * - blockOnSuccess evaluation
 * - i18n message resolution for dynamic error messages
 */
@Injectable({ providedIn: 'root' })
export class PreFetchCheckService {
  constructor(
    private http: HttpClient,
    private translateService: TranslateService
  ) {}

  /**
   * Executes all enabled pre-fetch checks sequentially.
   * Stops on first failure.
   */
  async executeChecks(
    checks: IPreFetchCheckConfig[],
    inputValue: string,
    controlValueResolver: ControlValueResolver,
    environment: IEnvironment
  ): Promise<IPreFetchCheckResult> {
    for (const check of checks) {
      const result = await this.executeSingleCheck(
        check,
        inputValue,
        controlValueResolver,
        environment
      );
      if (!result.passed) return result;
    }
    return { passed: true };
  }

  /**
   * Executes a single pre-fetch check.
   */
  private async executeSingleCheck(
    check: IPreFetchCheckConfig,
    inputValue: string,
    controlValueResolver: ControlValueResolver,
    environment: IEnvironment
  ): Promise<IPreFetchCheckResult> {
    let url = check.url;
    if (check.useBaseUrl) {
      url = updateUrlWithApiGatewayBaseUrl(url, environment);
    }

    const payload = this.buildPayload(check, inputValue, controlValueResolver);
    const method = check.method ?? 'POST';

    try {
      let response: IIntegrationFetchSyncResponse;

      if (method === 'GET') {
        response = await lastValueFrom(
          this.http.get<IIntegrationFetchSyncResponse>(url, {
            headers: { 'Endpoint-Code': check.endpointCode },
          })
        );
      } else {
        const params: IIntegrationFetchSyncRequest = {
          endpointCode: check.endpointCode,
          callerId: uuidv4(),
          payload,
          requester: uuidv4(),
        };
        response = await lastValueFrom(
          this.http.post<IIntegrationFetchSyncResponse>(url, params)
        );
      }

      // Evaluate blockOnSuccess
      if (check.blockOnSuccess) {
        const parsedInnerResponse = this.parseInnerResponse(response);
        const fieldValue = this.getNestedValue(
          parsedInnerResponse,
          check.blockOnSuccess.responseField
        );

        if (fieldValue === check.blockOnSuccess.value) {
          const message = this.resolveBlockOnSuccessMessage(
            check,
            parsedInnerResponse
          );
          return {
            passed: false,
            errorKey: check.errorKey,
            errorMessage: message,
          };
        }
      }

      return { passed: true };
    } catch (httpError: unknown) {
      const errorBody = (httpError as HttpErrorResponse)
        ?.error as IIntegrationErrorBody;
      const message = this.extractErrorMessage(errorBody, check);
      return {
        passed: false,
        errorKey: check.errorKey,
        errorMessage: message,
      };
    }
  }

  // ============================================================
  // Payload
  // ============================================================

  private buildPayload(
    check: IPreFetchCheckConfig,
    inputValue: string,
    controlValueResolver: ControlValueResolver
  ): Record<string, unknown> {
    if (!check.payloadKeyMappings?.length) {
      return {};
    }

    const payload: Record<string, unknown> = {};
    check.payloadKeyMappings.forEach((mapping: IPreFetchPayloadKeyMapping) => {
      if (mapping.isStatic) {
        payload[mapping.key] = mapping.staticValue;
      } else if (mapping.sourceField) {
        const value = controlValueResolver(
          mapping.sourceField,
          mapping.searchInRootForm
        );
        payload[mapping.key] = value ?? inputValue;
      } else {
        payload[mapping.key] = inputValue;
      }
    });
    return payload;
  }

  // ============================================================
  // Response parsing
  // ============================================================

  /**
   * Parses the inner `data.response` JSON string from the integration API.
   *
   * The `/integration/v1/fetch/sync` endpoint wraps the actual response as a
   * JSON-encoded string in `data.response`. This method extracts and parses it
   * so that dot-notation paths in blockOnSuccess config resolve correctly.
   *
   * Falls back to the raw `data` object if `response` is already an object
   * or if parsing fails.
   */
  private parseInnerResponse(
    httpResponse: IIntegrationFetchSyncResponse
  ): Record<string, unknown> {
    const data = httpResponse?.data;
    if (!data) return {};

    const innerResponse: unknown = data.response;

    // Defensive: backend may return an already-parsed object at runtime
    if (innerResponse && typeof innerResponse === 'object') {
      return innerResponse as Record<string, unknown>;
    }

    // Standard case: JSON string that needs parsing
    if (typeof innerResponse === 'string') {
      try {
        return JSON.parse(innerResponse) as Record<string, unknown>;
      } catch {
        return {};
      }
    }

    return {};
  }

  // ============================================================
  // Message resolution
  // ============================================================

  /**
   * Resolves the display message for a blockOnSuccess condition.
   *
   * Resolution order:
   * 1. messageTranslationKey + messageTranslationParams (translated with dynamic values)
   * 2. messagePath (raw string from parsed response)
   * 3. IPreFetchCheckConfig.errorMessage (static fallback)
   */
  private resolveBlockOnSuccessMessage(
    check: IPreFetchCheckConfig,
    parsedInnerResponse: Record<string, unknown>
  ): string | undefined {
    const blockConfig = check.blockOnSuccess;
    if (!blockConfig) return check.errorMessage;

    // Priority 1: Translated message with interpolated params
    if (blockConfig.messageTranslationKey) {
      const translationParams: Record<string, unknown> = {};

      if (blockConfig.messageTranslationParams) {
        for (const [paramKey, responsePath] of Object.entries(
          blockConfig.messageTranslationParams
        )) {
          translationParams[paramKey] =
            this.getNestedValue(parsedInnerResponse, responsePath) ?? '';
        }
      }

      return this.translateService.instant(
        blockConfig.messageTranslationKey,
        translationParams
      );
    }

    // Priority 2: Raw message from response path
    if (blockConfig.messagePath) {
      const rawMessage = this.getNestedValue(
        parsedInnerResponse,
        blockConfig.messagePath
      );
      if (rawMessage && typeof rawMessage === 'string') return rawMessage;
    }

    // Priority 3: Static fallback
    return check.errorMessage;
  }

  /**
   * Extracts the best error message from an HTTP error body.
   * Resolution order:
   * 1. errorMessagePath (custom dot-notation path)
   * 2. error.message (common pattern)
   * 3. message (fallback pattern)
   * 4. errorMessage (static config fallback)
   */
  private extractErrorMessage(
    errorBody: IIntegrationErrorBody | undefined,
    check: IPreFetchCheckConfig
  ): string | undefined {
    if (!errorBody) return check.errorMessage;

    if (check.errorMessagePath) {
      const extracted = this.getNestedValue(errorBody, check.errorMessagePath);
      if (extracted && typeof extracted === 'string') return extracted;
    }

    if (errorBody?.error?.message) return errorBody.error.message;
    if (errorBody?.message) return errorBody.message;

    return check.errorMessage;
  }

  // ============================================================
  // Utilities
  // ============================================================

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private getNestedValue(obj: any, path: string): unknown {
    if (!obj || !path) return obj;
    return path
      .split('.')
      .reduce((current: any, prop: string) => current?.[prop], obj);
  }
}
